import React, { useState, useEffect } from "react";
import { db, auth } from "../../firebase";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  deleteDoc,
  writeBatch,
  updateDoc,
  getDoc,
  increment
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";

function DeleteTeamsPage() {
  const [user] = useAuthState(auth);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  // Fetch teams where the current user is an admin
  useEffect(() => {
    const fetchTeams = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      try {
        // Query teams where user is a member (to match security rules)
        const teamsQuery = query(
          collection(db, "teams"),
          where("members", "array-contains", user.uid)
        );
        const teamsSnapshot = await getDocs(teamsQuery);
        
        // Filter to only teams where user is admin or coach
        const teamsData = teamsSnapshot.docs
          .map((docSnap) => {
            const data = docSnap.data();
            return {
              id: docSnap.id,
              ...data,
              // Check if user is admin or coach
              isAdmin: data.admins?.includes(user.uid) || data.coachId === user.uid
            };
          })
          .filter(team => team.isAdmin); // Only keep teams where user is admin
          
        setTeams(teamsData);
      } catch (error) {
        console.error("Error fetching teams:", error);
        showNotification("Failed to load teams. Please try again.", "error");
      }
      setLoading(false);
    };

    fetchTeams();
  }, [user]);

  // Helper function to show notifications
  const showNotification = (message, type) => {
    const notification = document.getElementById("notification");
    if (notification) {
      notification.textContent = message;
      notification.className = `notification ${type}`;
      notification.style.opacity = "1";
      
      setTimeout(() => {
        notification.style.opacity = "0";
      }, 3000);
    }
  };

  // Function to handle deletion of a team and its related data
  const handleDelete = async (teamId, teamName) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${teamName}"? This action cannot be undone and will delete all data associated with this team.`
    );
    if (!confirmDelete) return;

    setDeleteInProgress(true);
    try {
      // First, get the team to confirm the current user is an admin
      const teamRef = doc(db, "teams", teamId);
      const teamDoc = await getDoc(teamRef);
      
      if (!teamDoc.exists()) {
        throw new Error("Team not found");
      }
      
      const teamData = teamDoc.data();
      
      // Check if user is coach or admin
      const isCoach = teamData.coachId === user.uid;
      const isAdmin = teamData.admins?.includes(user.uid) || false;
      
      if (!isCoach && !isAdmin) {
        throw new Error("You don't have permission to delete this team");
      }
      
      // Get user document to update the team count
      const userRef = doc(db, "users", user.uid);
      
      console.log(`Attempting to delete team: ${teamId}, user is ${isCoach ? 'coach' : 'admin'}`);

      // Try to find and delete rosters FIRST before deleting the team
      try {
        const rostersQuery = query(
          collection(db, "rosters"),
          where("teamId", "==", teamId)
        );
        const rostersSnapshot = await getDocs(rostersQuery);
        
        if (!rostersSnapshot.empty) {
          console.log(`Found ${rostersSnapshot.size} rosters to delete`);
          
          // Delete each roster document individually
          for (const rosterDoc of rostersSnapshot.docs) {
            try {
              await deleteDoc(doc(db, "rosters", rosterDoc.id));
              console.log(`Deleted roster: ${rosterDoc.id}`);
            } catch (rosterError) {
              console.log(`Failed to delete roster ${rosterDoc.id}:`, rosterError);
              // Continue with other rosters even if one fails
            }
          }
        } else {
          console.log("No rosters found for this team");
        }
      } catch (rostersError) {
        // This is fine - it's possible we don't have permission to read rosters
        // or there are no rosters for this team
        console.log("Note: Could not access rosters before team deletion:", rostersError.message);
      }
      
      // Now delete the team
      await deleteDoc(teamRef);
      console.log(`Team ${teamId} deleted successfully`);
      
      // Update user's team count
      await updateDoc(userRef, {
        "subscription.teamsCount": increment(-1)
      });
      console.log("User team count updated successfully");
      
      // Update local state to remove the deleted team
      setTeams(teams.filter((team) => team.id !== teamId));
      
      // Show success notification
      showNotification(`Team "${teamName}" deleted successfully.`, "success");
    } catch (error) {
      console.error("Error deleting team:", error);
      showNotification(error.message || "Failed to delete team. Please try again.", "error");
    } finally {
      setDeleteInProgress(false);
    }
  };

  // Filter teams based on search term
  const filteredTeams = teams.filter((team) => 
    team.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sort teams by name
  const sortedTeams = [...filteredTeams].sort((a, b) =>
    (a.name || "").localeCompare(b.name || "")
  );

  if (loading) {
    return (
      <div
        className="loading-container"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          gap: "1rem",
        }}
      >
        <div
          className="spinner"
          style={{
            width: "40px",
            height: "40px",
            border: "4px solid rgba(0, 0, 0, 0.1)",
            borderLeft: "4px solid #1976D2",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        ></div>
        <p style={{ color: "#555", fontSize: "1rem" }}>Loading your teams...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }
  
  if (!user) {
    return (
      <div
        style={{
          maxWidth: "600px",
          margin: "4rem auto",
          padding: "2rem",
          textAlign: "center",
          background: "#fff",
          borderRadius: "10px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
        }}
      >
        <h2 style={{ color: "#333", marginBottom: "1rem" }}>Access Restricted</h2>
        <p style={{ color: "#555", marginBottom: "2rem" }}>
          Please log in to view and manage your teams.
        </p>
        <button
          style={{
            background: "linear-gradient(135deg, #1976D2, #0D47A1)",
            color: "#fff",
            border: "none",
            borderRadius: "5px",
            padding: "0.75rem 1.5rem",
            fontSize: "1rem",
            cursor: "pointer",
            transition: "transform 0.2s, background 0.3s",
          }}
          onClick={() => navigate("/login")}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.05)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
          }}
        >
          Go to Login
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        maxWidth: "1000px",
        margin: "2rem auto",
        padding: "2rem",
        fontFamily: "'Roboto', sans-serif",
        background: "#f9f9f9",
        borderRadius: "15px",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
      }}
    >
      <h2
        style={{
          textAlign: "center",
          marginBottom: "2rem",
          color: "#0D47A1",
          fontSize: "2rem",
          fontWeight: "500",
        }}
      >
        Manage Your Teams
      </h2>

      {/* Search control */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginBottom: "1.5rem",
        }}
      >
        <div
          style={{
            position: "relative",
            flex: "1",
            maxWidth: "400px",
          }}
        >
          <input
            type="text"
            placeholder="Search teams..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: "100%",
              padding: "0.75rem 1rem 0.75rem 2.5rem",
              fontSize: "1rem",
              border: "1px solid #e0e0e0",
              borderRadius: "5px",
              boxShadow: "0 2px 5px rgba(0, 0, 0, 0.05)",
            }}
          />
          <div
            style={{
              position: "absolute",
              left: "0.75rem",
              top: "50%",
              transform: "translateY(-50%)",
              color: "#777",
            }}
          >
            🔍
          </div>
        </div>
      </div>

      {/* Teams list */}
      {sortedTeams.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "3rem 1rem",
            background: "#fff",
            borderRadius: "10px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
            color: "#555",
          }}
        >
          {searchTerm ? (
            <p>No teams found matching "{searchTerm}".</p>
          ) : (
            <p>You don't have any teams to delete. Create a team first.</p>
          )}
          <button
            style={{
              background: "linear-gradient(135deg, #4CAF50, #2E7D32)",
              color: "#fff",
              border: "none",
              borderRadius: "5px",
              padding: "0.75rem 1.5rem",
              fontSize: "1rem",
              cursor: "pointer",
              marginTop: "1.5rem",
              transition: "transform 0.2s",
            }}
            onClick={() => navigate("/create-team")}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.05)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            Create New Team
          </button>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: "1.5rem",
          }}
        >
          {sortedTeams.map((team) => (
            <div
              key={team.id}
              style={{
                background: "#ffffff",
                border: "1px solid #e0e0e0",
                borderRadius: "10px",
                padding: "1.5rem",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                transition: "transform 0.2s, box-shadow 0.3s",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                height: "100%",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-5px)";
                e.currentTarget.style.boxShadow =
                  "0 12px 20px rgba(0, 0, 0, 0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 2px 8px rgba(0, 0, 0, 0.1)";
              }}
            >
              <div>
                <h3
                  style={{
                    margin: "0 0 0.5rem 0",
                    color: "#0D47A1",
                    fontSize: "1.2rem",
                  }}
                >
                  {team.name}
                </h3>
                <div
                  style={{
                    display: "inline-block",
                    background: team.coachId === user.uid ? "#e3f2fd" : "#f1f8e9",
                    color: team.coachId === user.uid ? "#1976D2" : "#558B2F",
                    padding: "0.25rem 0.5rem",
                    borderRadius: "12px",
                    fontSize: "0.75rem",
                    fontWeight: "500",
                    marginBottom: "0.5rem"
                  }}
                >
                  {team.coachId === user.uid ? "Coach" : "Admin"}
                </div>
                {team.sport && (
                  <p
                    style={{
                      margin: "0.5rem 0",
                      color: "#555",
                      fontSize: "0.9rem",
                    }}
                  >
                    Sport: {team.sport}
                  </p>
                )}
                {team.ageGroup && (
                  <p
                    style={{
                      margin: "0.5rem 0",
                      color: "#555",
                      fontSize: "0.9rem",
                    }}
                  >
                    Age Group: {team.ageGroup}
                  </p>
                )}
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: "1.5rem",
                  gap: "0.5rem",
                }}
              >
                <button
                  style={{
                    background: "#f5f5f5",
                    color: "#333",
                    border: "1px solid #e0e0e0",
                    borderRadius: "5px",
                    padding: "0.5rem",
                    fontSize: "0.9rem",
                    cursor: "pointer",
                    transition: "background 0.3s",
                    flex: "1",
                  }}
                  onClick={() => navigate(`/edit-team/${team.id}`)}
                >
                  Edit
                </button>
                <button
                  style={{
                    background: "linear-gradient(135deg, #d32f2f, #f44336)",
                    color: "#fff",
                    border: "none",
                    borderRadius: "5px",
                    padding: "0.5rem",
                    fontSize: "0.9rem",
                    cursor: "pointer",
                    transition: "background 0.3s, opacity 0.3s",
                    flex: "1",
                    opacity: deleteInProgress ? "0.7" : "1",
                  }}
                  onClick={() => handleDelete(team.id, team.name)}
                  disabled={deleteInProgress}
                >
                  {deleteInProgress ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Action buttons */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "2.5rem",
          gap: "1rem",
        }}
      >
        <button
          style={{
            background: "#f5f5f5",
            color: "#333",
            border: "1px solid #e0e0e0",
            borderRadius: "5px",
            padding: "0.75rem 1.5rem",
            fontSize: "1rem",
            cursor: "pointer",
            transition: "transform 0.2s, background 0.3s",
            boxShadow: "0 2px 5px rgba(0, 0, 0, 0.05)",
          }}
          onClick={() => navigate("/my-teams")}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.05)";
            e.currentTarget.style.background = "#eeeeee";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.background = "#f5f5f5";
          }}
        >
          Back to My Teams
        </button>

        <button
          style={{
            background: "linear-gradient(135deg, #4CAF50, #2E7D32)",
            color: "#fff",
            border: "none",
            borderRadius: "5px",
            padding: "0.75rem 1.5rem",
            fontSize: "1rem",
            cursor: "pointer",
            transition: "transform 0.2s, background 0.3s",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          }}
          onClick={() => navigate("/create-team")}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.05)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
          }}
        >
          Create New Team
        </button>
      </div>

      {/* Notification component */}
      <div
        id="notification"
        className="notification"
        style={{
          position: "fixed",
          bottom: "20px",
          right: "20px",
          padding: "1rem 2rem",
          borderRadius: "5px",
          color: "#fff",
          opacity: "0",
          transition: "opacity 0.3s ease",
          zIndex: "1000",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        }}
      ></div>

      {/* CSS for notifications */}
      <style>{`
        .notification.success {
          background-color: #4CAF50;
        }
        .notification.error {
          background-color: #F44336;
        }
      `}</style>
    </div>
  );
}

export default DeleteTeamsPage;